import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Inbox, LayoutGrid, CalendarDays, Armchair } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import CourseForm from "@/components/admin/CourseForm";
import EventForm from "@/components/admin/EventForm";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("courses");
  const [courses, setCourses] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [events, setEvents] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const loadCourses = () => api.get("/admin/courses").then((r) => setCourses(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadEnquiries = () => api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadEvents = () => api.get("/admin/events").then((r) => setEvents(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadRsvps = () => api.get("/admin/rsvps").then((r) => setRsvps(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    loadCourses();
    loadEnquiries();
    loadEvents();
    loadRsvps();
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const deleteCourse = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? This removes its public page.`)) return;
    try {
      await api.delete(`/admin/courses/${c.id}`);
      toast.success("Program deleted.");
      loadCourses();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteEnquiry = async (en) => {
    try {
      await api.delete(`/admin/enquiries/${en.id}`);
      toast.success("Enquiry deleted.");
      loadEnquiries();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await api.delete(`/admin/events/${ev.id}`);
      toast.success("Event deleted.");
      loadEvents();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteRsvp = async (r) => {
    try {
      await api.delete(`/admin/rsvps/${r.id}`);
      toast.success("RSVP deleted.");
      loadRsvps();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const tabBtn = (key, label, Icon, testid, count) => (
    <button
      key={key}
      onClick={() => setTab(key)}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2 font-mono2 text-[11px] tracking-[0.15em] border transition-colors duration-200 ${
        tab === key ? "bg-lima-ink text-white border-lima-ink" : "border-black/20 text-black/60 hover:text-black"
      }`}
    >
      <Icon size={15} /> {label}
      {count != null && <span className="font-mono2 text-[10px] opacity-70">({count})</span>}
    </button>
  );

  return (
    <div className="min-h-screen bg-lima-cream text-lima-ink font-body" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-lima-cream/85 backdrop-blur-xl border-b border-black/10">
        <div className="px-5 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <img src="/lima-logo.png" alt="LIMA — Life Inside Media Academy" className="h-9 w-auto" />
            <span className="font-mono2 text-[9px] tracking-[0.22em] text-black/50 border-l border-black/20 pl-3">ADMIN CONSOLE</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:block font-mono2 text-[11px] text-black/50" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-black/20 px-4 py-2 font-mono2 text-[11px] tracking-[0.15em] hover:bg-black hover:text-white transition-colors duration-200">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="px-5 md:px-10 py-8 md:py-12 max-w-7xl">
        <div className="flex flex-wrap items-center gap-3 mb-8">
          {tabBtn("courses", "Programs", LayoutGrid, "admin-tab-courses", courses.length)}
          {tabBtn("events", "Events", CalendarDays, "admin-tab-events", events.length)}
          {tabBtn("rsvps", "RSVPs", Armchair, "admin-tab-rsvps", rsvps.length)}
          {tabBtn("enquiries", "Enquiries", Inbox, "admin-tab-enquiries", enquiries.length)}
        </div>

        {tab === "courses" && (
          <section data-testid="admin-courses-panel">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display font-bold text-3xl tracking-tight">Programs</h1>
              <button
                onClick={() => { setEditing(null); setFormOpen(true); }}
                data-testid="admin-new-course-button"
                className="flex items-center gap-2 bg-lima-blue hover:bg-lima-bluehover text-white px-5 py-3 font-mono2 text-[11px] font-bold tracking-[0.2em] transition-colors duration-200"
              >
                <Plus size={15} /> New Program
              </button>
            </div>
            <div className="border border-black/15 bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-courses-table">
                <thead>
                  <tr className="border-b border-black/15 font-mono2 text-[11px] tracking-[0.2em] text-black/50 text-left">
                    <th className="px-4 py-3 font-medium">TITLE</th>
                    <th className="px-4 py-3 font-medium">TYPE</th>
                    <th className="px-4 py-3 font-medium">FEE</th>
                    <th className="px-4 py-3 font-medium">STATUS</th>
                    <th className="px-4 py-3 font-medium">PAGE</th>
                    <th className="px-4 py-3 font-medium text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((c) => (
                    <tr key={c.id} className="border-b border-black/10 last:border-0 hover:bg-black/[0.02]" data-testid={`admin-course-row-${c.slug}`}>
                      <td className="px-4 py-3 font-semibold">{c.title}</td>
                      <td className="px-4 py-3">
                        <span className={`font-mono2 text-[11px] tracking-[0.15em] px-2 py-1 ${c.kind === "workshop" ? "bg-lima-red/10 text-lima-red" : "bg-lima-blue/10 text-lima-blue"}`}>
                          {c.kind === "workshop" ? "WORKSHOP" : "COURSE"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-black/70">{c.fee || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`font-mono2 text-[11px] tracking-[0.15em] ${c.published ? "text-green-700" : "text-black/40"}`}>
                          {c.published ? "● LIVE" : "○ DRAFT"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/programs/${c.slug}`} target="_blank" className="font-mono2 text-xs text-lima-blue hover:underline" data-testid={`admin-view-${c.slug}`}>
                          /programs/{c.slug}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => { setEditing(c); setFormOpen(true); }} data-testid={`admin-edit-${c.slug}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-black/70 hover:text-lima-blue mr-4">
                          <Pencil size={13} /> Edit
                        </button>
                        <button onClick={() => deleteCourse(c)} data-testid={`admin-delete-${c.slug}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-black/70 hover:text-lima-red">
                          <Trash2 size={13} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {courses.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center font-mono2 text-xs text-black/40">No programs yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "events" && (
          <section data-testid="admin-events-panel">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display font-bold text-3xl tracking-tight">Events &amp; Workshops</h1>
              <button
                onClick={() => { setEditingEvent(null); setEventFormOpen(true); }}
                data-testid="admin-new-event-button"
                className="flex items-center gap-2 bg-lima-blue hover:bg-lima-bluehover text-white px-5 py-3 font-mono2 text-[11px] font-bold tracking-[0.2em] transition-colors duration-200"
              >
                <Plus size={15} /> New Event
              </button>
            </div>
            <div className="border border-black/15 bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-events-table">
                <thead>
                  <tr className="border-b border-black/15 font-mono2 text-[11px] tracking-[0.2em] text-black/50 text-left">
                    <th className="px-4 py-3 font-medium">EVENT</th>
                    <th className="px-4 py-3 font-medium">TYPE</th>
                    <th className="px-4 py-3 font-medium">DATE</th>
                    <th className="px-4 py-3 font-medium">LOCATION</th>
                    <th className="px-4 py-3 font-medium">STATUS</th>
                    <th className="px-4 py-3 font-medium text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-b border-black/10 last:border-0 hover:bg-black/[0.02]" data-testid={`admin-event-row-${ev.id}`}>
                      <td className="px-4 py-3 font-semibold">{ev.title}</td>
                      <td className="px-4 py-3">
                        <span className="font-mono2 text-[11px] tracking-[0.15em] px-2 py-1 bg-lima-blue/10 text-lima-blue">{ev.kind}</span>
                      </td>
                      <td className="px-4 py-3 text-black/70 font-mono2 text-xs">{ev.date_text || "—"}</td>
                      <td className="px-4 py-3 text-black/70">{ev.location || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`font-mono2 text-[11px] tracking-[0.15em] ${ev.published ? "text-green-700" : "text-black/40"}`}>
                          {ev.published ? "● LIVE" : "○ DRAFT"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => { setEditingEvent(ev); setEventFormOpen(true); }} data-testid={`admin-event-edit-${ev.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-black/70 hover:text-lima-blue mr-4">
                          <Pencil size={13} /> Edit
                        </button>
                        <button onClick={() => deleteEvent(ev)} data-testid={`admin-event-delete-${ev.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-black/70 hover:text-lima-red">
                          <Trash2 size={13} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {events.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center font-mono2 text-xs text-black/40">No events yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "rsvps" && (
          <section data-testid="admin-rsvps-panel">
            <h1 className="font-display font-bold text-3xl tracking-tight mb-6">Event RSVPs</h1>
            <div className="border border-black/15 bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-rsvps-table">
                <thead>
                  <tr className="border-b border-black/15 font-mono2 text-[11px] tracking-[0.2em] text-black/50 text-left">
                    <th className="px-4 py-3 font-medium">DATE</th>
                    <th className="px-4 py-3 font-medium">NAME</th>
                    <th className="px-4 py-3 font-medium">PHONE</th>
                    <th className="px-4 py-3 font-medium">EMAIL</th>
                    <th className="px-4 py-3 font-medium">EVENT</th>
                    <th className="px-4 py-3 font-medium text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {rsvps.map((r) => (
                    <tr key={r.id} className="border-b border-black/10 last:border-0 hover:bg-black/[0.02]" data-testid={`admin-rsvp-row-${r.id}`}>
                      <td className="px-4 py-3 font-mono2 text-xs text-black/50 whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-4 py-3 font-semibold whitespace-nowrap">{r.name}</td>
                      <td className="px-4 py-3 text-black/70">{r.phone || "—"}</td>
                      <td className="px-4 py-3 text-black/70">{r.email}</td>
                      <td className="px-4 py-3 text-black/70 max-w-xs"><p className="line-clamp-2">{r.event}</p></td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => deleteRsvp(r)} data-testid={`admin-rsvp-delete-${r.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-black/70 hover:text-lima-red">
                          <Trash2 size={13} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {rsvps.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center font-mono2 text-xs text-black/40">No RSVPs yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "enquiries" && (
          <section data-testid="admin-enquiries-panel">
            <h1 className="font-display font-bold text-3xl tracking-tight mb-6">Enquiries</h1>
            <div className="border border-black/15 bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-enquiries-table">
                <thead>
                  <tr className="border-b border-black/15 font-mono2 text-[11px] tracking-[0.2em] text-black/50 text-left">
                    <th className="px-4 py-3 font-medium">DATE</th>
                    <th className="px-4 py-3 font-medium">NAME</th>
                    <th className="px-4 py-3 font-medium">CONTACT</th>
                    <th className="px-4 py-3 font-medium">PROGRAM</th>
                    <th className="px-4 py-3 font-medium">MESSAGE</th>
                    <th className="px-4 py-3 font-medium text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((en) => (
                    <tr key={en.id} className="border-b border-black/10 last:border-0 hover:bg-black/[0.02] align-top" data-testid={`admin-enquiry-row-${en.id}`}>
                      <td className="px-4 py-3 font-mono2 text-xs text-black/50 whitespace-nowrap">
                        {en.created_at ? new Date(en.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-4 py-3 font-semibold whitespace-nowrap">{en.name}</td>
                      <td className="px-4 py-3 text-black/70">
                        <div>{en.email}</div>
                        {en.phone && <div className="text-black/50 text-xs">{en.phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-black/70 whitespace-nowrap">{en.program || "—"}</td>
                      <td className="px-4 py-3 text-black/70 max-w-xs"><p className="line-clamp-3">{en.message || "—"}</p></td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => deleteEnquiry(en)} data-testid={`admin-enquiry-delete-${en.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-black/70 hover:text-lima-red">
                          <Trash2 size={13} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {enquiries.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center font-mono2 text-xs text-black/40">No enquiries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      <CourseForm open={formOpen} onOpenChange={setFormOpen} course={editing} onSaved={loadCourses} />
      <EventForm open={eventFormOpen} onOpenChange={setEventFormOpen} event={editingEvent} onSaved={loadEvents} />
    </div>
  );
}
